<template>
  <suspense v-if="!isApproved">
    <a-focus-trap
      initial-focus="[data-cookiebanner-approve]"
      :is-enabled="isFocusTrapEnabled"
    >
      <section
        class="cookiebanner"
        :class="{ 'cookiebanner--has-settings-open': hasSettingsOpen }"
        aria-modal="true"
        role="dialog"
      >
        <div class="cookiebanner__inner spacer-horizontal">
          <div class="cookiebanner__content">
            <template v-if="hasSettingsOpen">
              <dpc-form
                v-model:fields="settings"
                :before-submit-hook="beforeSubmitHook"
              >
                <slot name="settings" />
              </dpc-form>
            </template>

            <slot v-else name="home" />
          </div>

          <slot />
        </div>
      </section>
    </a-focus-trap>
  </suspense>
</template>
<script setup>
import { onBeforeUnmount, provide, readonly, ref, toRefs, watch } from 'vue';
import { useCookies } from '@vueuse/integrations/useCookies';
import { useEventBus, whenever } from '@vueuse/core';
import dayjs from 'dayjs';

const props = defineProps({
  version: {
    type: String,
    default: '1',
  },
});

const { version } = toRefs(props);

const COOKIE_NAMES = {
  approved: `cookiebanner-approved-v${version.value}`,
  approvedAll: `cookiebanner-approved-all-v${version.value}`,
  settings: `cookiebanner-settings-v${version.value}`,
};

const cookie = useCookies(Object.values(COOKIE_NAMES));
const isApproved = ref(cookie.get(COOKIE_NAMES.approved) || false);
const isApprovedAll = ref(cookie.get(COOKIE_NAMES.approvedAll) || false);
const settings = ref(cookie.get(COOKIE_NAMES.settings) || {});
const hasSettingsOpen = ref(false);

const toggleSettingsOpen = () => {
  hasSettingsOpen.value = !hasSettingsOpen.value;
};

watch(hasSettingsOpen, hasSettingsOpen => {
  const html = document.querySelector('html');
  html.classList[hasSettingsOpen ? 'add' : 'remove'](
    'is-locked-by-dpc-cookiebanner'
  );
});

watch(
  isApproved,
  isApproved => {
    const html = document.querySelector('html');
    html.classList[!isApproved ? 'add' : 'remove'](
      'dpc-cookiebanner-is-visible'
    );
  },
  { immediate: true }
);

provide('cookiebanner/hasSettingsOpen', readonly(hasSettingsOpen));
provide('cookiebanner/toggleSettingsOpen', toggleSettingsOpen);

function getDomain() {
  const hostname = window.location.hostname.split('.');
  hostname.reverse();
  return `${hostname[1]}.${hostname[0]}`;
}

const cookieOptions = {
  expires: dayjs().add(1, 'year').toDate(),
  sameSite: true,
  path: '/',
  domain: getDomain(),
};

const approve = ({ approveAll = false, setCookies = true }) => {
  if (setCookies) {
    cookie.set(COOKIE_NAMES.approved, true, cookieOptions);
    cookie.set(COOKIE_NAMES.approvedAll, approveAll, cookieOptions);
    cookie.set(COOKIE_NAMES.settings, settings.value, cookieOptions);
  }

  isApprovedAll.value = approveAll;
  isApproved.value = true;
};
provide('cookiebanner/approve', approve);

const revoke = () => {
  isApproved.value = false;
  isApprovedAll.value = false;
  settings.value = {};
  hasSettingsOpen.value = false;

  cookie.remove(COOKIE_NAMES.approved, cookieOptions);
  cookie.remove(COOKIE_NAMES.approvedAll, cookieOptions);
  cookie.remove(COOKIE_NAMES.settings, cookieOptions);

  const DPC_SETTINGS = window.$DPC_SETTINGS || window.DPC_SETTINGS;

  DPC_SETTINGS?.onCookiebannerRevoke();
};
provide('cookiebanner/revoke', revoke);

whenever(
  isApproved,
  () => {
    const DPC_SETTINGS = window.$DPC_SETTINGS || window.DPC_SETTINGS;

    DPC_SETTINGS?.onCookiebannerApprove({
      approveAll: isApprovedAll.value,
      settings: settings.value,
    });
  },
  { immediate: true }
);

const beforeSubmitHook = () => approve();

const eventBus = useEventBus('cookiebanner');
eventBus.on(event => {
  if (event === 'revoke') {
    return revoke();
  }
});
</script>
